import * as React from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  Camera,
  TrendingUp,
  TrendingDown,
  Loader2,
  ArrowRight,
  History,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Card,
  CardHeader,
  CardContent,
  CardDescription,
} from "./card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./dialog";
import { Heading, Text, Label, MonoText } from "./typography";
import {
  CurrencyValue,
  PercentValue,
  AnimatedValue,
} from "./value-display";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { API_BASE_URL } from '../../lib/api';

/**
 * SnapshotManagement Component
 *
 * Original (pre-Institutional-Console) compact snapshot management card:
 * - Create new snapshots with one click
 * - Visual line chart showing portfolio value over time
 * - Summary showing snapshot count, latest value, and overall change
 * - Click on chart to view detailed breakdown
 * - Link to view all snapshots
 *
 * As of issue #235, the V2 Dashboard no longer renders this card directly —
 * it re-implements the same create/chart/summary/click-to-detail behavior
 * inline as the Institutional Console "Snapshot history" strip
 * (`pages/v2/Dashboard.jsx`'s `SnapshotHistoryStrip`), which shares this
 * module's `SnapshotDetailsDialog` export (see below) rather than forking a
 * second copy. `SnapshotManagement` itself is kept as a standalone,
 * still-exported component (not deleted) in case any other screen wants the
 * pre-redesign compact-card layout; it is not currently referenced by any
 * page.
 *
 * Design: Fixed height chart for consistent layout
 */

// Helper to format dates for chart axis
function formatChartDate(dateString) {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// Helper to format dates for tooltip
function formatTooltipDate(dateString) {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Format Y-axis values
function formatYAxis(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
  return value.toFixed(0);
}

// Custom tooltip for the chart
function ChartTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="rounded-lg border bg-background p-2 shadow-md">
        <p className="text-xs text-muted-foreground mb-1">
          {formatTooltipDate(data.date)}
        </p>
        <p className="text-sm font-semibold font-mono">
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "EUR",
          }).format(data.value)}
        </p>
      </div>
    );
  }
  return null;
}

// Calculate overall change between first and last snapshot
function calculateOverallChange(snapshots) {
  if (!snapshots || snapshots.length < 2) return null;
  const oldest = snapshots[snapshots.length - 1];
  const newest = snapshots[0];
  const oldValue = oldest.netWorth || oldest.totalValue || 0;
  const newValue = newest.netWorth || newest.totalValue || 0;
  if (oldValue === 0) return null;
  return ((newValue - oldValue) / oldValue) * 100;
}

// Snapshot details modal
//
// Exported (not just module-local) as of issue #235: the Institutional
// Console Dashboard's "Snapshot history" strip re-implements the compact
// chart/summary UI inline (per DESIGN-SPEC §5's horizontal-strip layout) but
// reuses this exact dialog for click-to-view snapshot detail, rather than
// forking a second copy of the same markup.
export function SnapshotDetailsDialog({ snapshot, open, onOpenChange }) {
  if (!snapshot) return null;

  const categories = snapshot.categories || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Snapshot Details
          </DialogTitle>
          <DialogDescription>
            Portfolio snapshot from {new Date(snapshot.date + "T00:00:00").toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-3">
              <Label className="text-xs text-muted-foreground">Net Worth</Label>
              <CurrencyValue
                value={snapshot.netWorth || snapshot.totalValue}
                currency="EUR"
                size="lg"
                className="block mt-1"
              />
            </div>
            <div className="rounded-lg border border-border p-3">
              <Label className="text-xs text-muted-foreground">Total Assets</Label>
              <CurrencyValue
                value={snapshot.totalAssets}
                currency="EUR"
                size="lg"
                className="block mt-1"
              />
            </div>
          </div>

          {snapshot.totalLiabilities > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <Label className="text-xs text-muted-foreground">Total Liabilities</Label>
              <CurrencyValue
                value={-snapshot.totalLiabilities}
                currency="EUR"
                size="lg"
                colorize
                className="block mt-1"
              />
            </div>
          )}

          {/* Category Breakdown */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Asset Breakdown</Label>
            <div className="space-y-1.5">
              {Object.entries(categories)
                .filter(([key, cat]) => key !== "liabilities" && cat.value > 0)
                .sort((a, b) => b[1].value - a[1].value)
                .map(([key, category]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between py-1 px-2 rounded bg-muted/50"
                  >
                    <Text size="sm" className="capitalize">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </Text>
                    <div className="flex items-center gap-3">
                      <MonoText size="xs" className="text-muted-foreground">
                        {category.percentage?.toFixed(1)}%
                      </MonoText>
                      <CurrencyValue
                        value={category.value}
                        currency="EUR"
                        size="sm"
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Benchmarks if available */}
          {snapshot.benchmarks && Object.keys(snapshot.benchmarks).some(k => snapshot.benchmarks[k]) && (
            <div>
              <Label className="text-sm font-medium mb-2 block">Market Benchmarks</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { key: "sp500", label: "S&P 500" },
                  { key: "nasdaq", label: "NASDAQ" },
                  { key: "dax", label: "DAX" },
                  { key: "msciWorld", label: "MSCI World" },
                ].map(({ key, label }) =>
                  snapshot.benchmarks[key] ? (
                    <div
                      key={key}
                      className="flex items-center justify-between py-1 px-2 rounded bg-muted/50"
                    >
                      <Text size="xs" className="text-muted-foreground">
                        {label}
                      </Text>
                      <MonoText size="xs">
                        {snapshot.benchmarks[key]?.toLocaleString("en-US", {
                          maximumFractionDigits: 2,
                        })}
                      </MonoText>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="text-xs text-muted-foreground border-t border-border pt-2 mt-2">
            Created: {new Date(snapshot.createdAt).toLocaleString()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Main SnapshotManagement Component
 */
export function SnapshotManagement({ className }) {
  const location = useLocation();
  const [snapshots, setSnapshots] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [createSuccess, setCreateSuccess] = React.useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = React.useState(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  // Fetch snapshots
  const fetchSnapshots = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/snapshots`, {
        withCredentials: true,
      });

      setSnapshots(response.data.snapshots || []);
    } catch (err) {
      console.error("Error fetching snapshots:", err);
      setError(err.response?.data?.error || "Failed to load snapshots");
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new snapshot. Wrapped in useCallback (stable, depends only on the
  // already-stable fetchSnapshots) so the "v2:take-snapshot" global listener
  // effect below can list it as a dependency without re-subscribing every
  // render (issue #238 ESLint cleanup — react-hooks/exhaustive-deps).
  const createSnapshot = React.useCallback(async () => {
    try {
      setCreating(true);
      setCreateSuccess(false);
      setError(null);

      await axios.post(
        `${API_BASE_URL}/snapshots/trigger`,
        {},
        { withCredentials: true }
      );

      setCreateSuccess(true);
      // Refresh snapshots list
      await fetchSnapshots();

      // Clear success message after 3 seconds
      setTimeout(() => setCreateSuccess(false), 3000);
    } catch (err) {
      console.error("Error creating snapshot:", err);
      setError(err.response?.data?.error || "Failed to create snapshot");
    } finally {
      setCreating(false);
    }
  }, [fetchSnapshots]);

  // View snapshot details (clicking on chart point)
  const handleViewDetails = (snapshot) => {
    setSelectedSnapshot(snapshot);
    setDetailsOpen(true);
  };

  // Fetch snapshots on mount and when navigating back to this page
  // Using location.key ensures refetch on every navigation to this route
  React.useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots, location.key]);

  // Listen for global "Take Snapshot" action from sidebar
  React.useEffect(() => {
    const handleTakeSnapshot = () => {
      createSnapshot();
    };

    window.addEventListener("v2:take-snapshot", handleTakeSnapshot);
    return () => {
      window.removeEventListener("v2:take-snapshot", handleTakeSnapshot);
    };
  }, [createSnapshot]);

  // Transform snapshots to chart data (sorted oldest to newest)
  const chartData = React.useMemo(() => {
    if (!snapshots || snapshots.length === 0) return [];
    return [...snapshots]
      .map((s) => ({
        date: s.date,
        value: s.netWorth || s.totalValue || 0,
        snapshot: s, // Keep reference for click handling
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [snapshots]);

  // Calculate chart domain for Y-axis
  const { minValue, maxValue } = React.useMemo(() => {
    if (!chartData || chartData.length === 0) return { minValue: 0, maxValue: 0 };
    const values = chartData.map((d) => d.value);
    return {
      minValue: Math.min(...values) * 0.98,
      maxValue: Math.max(...values) * 1.02,
    };
  }, [chartData]);

  // Calculate overall change percentage
  const overallChange = calculateOverallChange(snapshots);

  // Get latest snapshot value
  const latestValue = snapshots.length > 0
    ? (snapshots[0].netWorth || snapshots[0].totalValue || 0)
    : 0;

  return (
    <AnimatedValue delay={300} animation="slide-up">
      <Card className={cn("flex flex-col", className)}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded-md bg-primary/10 p-1.5">
                <History className="h-4 w-4 text-primary" />
              </div>
              <div>
                <Heading level={5} className="text-sm">Snapshot History</Heading>
                {/* Summary line */}
                {!loading && snapshots.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{snapshots.length} snapshot{snapshots.length !== 1 ? "s" : ""}</span>
                    <span>-</span>
                    <span>Latest: <CurrencyValue value={latestValue} size="xs" className="inline" /></span>
                    {overallChange !== null && (
                      <>
                        <span>-</span>
                        <span className="flex items-center gap-0.5">
                          {overallChange >= 0 ? (
                            <TrendingUp className="h-3 w-3 text-success" />
                          ) : (
                            <TrendingDown className="h-3 w-3 text-destructive" />
                          )}
                          <PercentValue
                            value={overallChange}
                            size="xs"
                            showSign
                            showTrend={false}
                          />
                        </span>
                      </>
                    )}
                  </div>
                )}
                {loading && (
                  <CardDescription className="text-xs">
                    Loading...
                  </CardDescription>
                )}
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={createSnapshot}
                    disabled={creating}
                    className="h-8"
                  >
                    {creating ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Camera className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {creating ? "Creating..." : "Snapshot"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Create a new portfolio snapshot
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>

        <CardContent className="pt-0 flex-1 flex flex-col">
          {/* Success/Error messages */}
          {createSuccess && (
            <div className="mb-2 py-1.5 px-2 rounded bg-success/10 border border-success/20">
              <Text size="xs" className="text-success">
                Snapshot created successfully!
              </Text>
            </div>
          )}

          {error && (
            <div className="mb-2 py-1.5 px-2 rounded bg-destructive/10 border border-destructive/20">
              <Text size="xs" className="text-destructive">
                {error}
              </Text>
            </div>
          )}

          {/* Loading state */}
          {loading ? (
            <div className="flex items-center justify-center h-[200px]">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : snapshots.length === 0 ? (
            /* Empty state */
            <div className="text-center py-6">
              <Camera className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <Text variant="secondary" size="sm">
                No snapshots yet
              </Text>
              <Text variant="secondary" size="xs" className="mt-1">
                Create your first snapshot to track portfolio history
              </Text>
            </div>
          ) : (
            /* Snapshot chart */
            <div className="w-full" style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                  onClick={(e) => {
                    if (e && e.activePayload && e.activePayload[0]) {
                      handleViewDetails(e.activePayload[0].payload.snapshot);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <defs>
                    <linearGradient id="snapshotColorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="var(--color-chart-primary, var(--color-primary-500))"
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor="var(--color-chart-primary, var(--color-primary-500))"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatChartDate}
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    className="text-muted-foreground"
                  />
                  <YAxis
                    tickFormatter={formatYAxis}
                    domain={[minValue, maxValue]}
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={45}
                    className="text-muted-foreground"
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="var(--color-chart-primary, var(--color-primary-500))"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#snapshotColorValue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* View all snapshots link */}
          {snapshots.length > 0 && (
            <Link
              to="/snapshots"
              className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              View all snapshots
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </CardContent>

        {/* Snapshot details dialog */}
        <SnapshotDetailsDialog
          snapshot={selectedSnapshot}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
        />
      </Card>
    </AnimatedValue>
  );
}

export default SnapshotManagement;
